import { useState } from 'react';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Button from '@material-ui/core/Button';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import {
    CREATED_AT_FIELD,
    CREATED_BY_FIELD,
    EXPIRY_FIELD,
    LAST_ACCESSED_FIELD,
    NAME_FIELD,
    TOKEN_ROLE_FIELD,
} from './ApiTokenConstants';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { buildCreatePageTarget, buildPageId } from '../AdminLinkUtils';
import { AdminPages } from '../AdminPages';
import Link from '../../links/Link';
import { ROLE_NAMES } from '../users/UserConstants';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import ConfirmButton from '../../form/ConfirmButton';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';

const apiDocsUrl = '/api-docs/';
const functionName = AdminPages.ApiTokens;

function getColumns() {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            mapping: NAME_FIELD,
            title: t`Name`,
            sortable: true,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: TOKEN_ROLE_FIELD,
            title: t`Role`,
            sortable: true,
            formatter: (role) => ROLE_NAMES[role],
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: LAST_ACCESSED_FIELD,
            title: t`Last Accessed`,
            sortable: true,
            width: 220,
            formatter: FORMATTER_TYPES.DATETIME,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: EXPIRY_FIELD,
            title: t`Expires`,
            sortable: true,
            width: 220,
            formatter: FORMATTER_TYPES.DATETIME,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: CREATED_AT_FIELD,
            title: t`Created`,
            sortable: true,
            width: 220,
            formatter: FORMATTER_TYPES.DATETIME,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: CREATED_BY_FIELD,
            title: t`Created By`,
            sortable: true,
            flex: 1,
        },
    ];
}

const initialSort = [{ property: NAME_FIELD, direction: SORT_ASC }];

const dataSource = {
    url: '/ngp/v2/apitokens',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [NAME_FIELD, TOKEN_ROLE_FIELD, CREATED_BY_FIELD],
    },
};

const revokeButtonText = i18n._(t`Revoke`);
const revokeTitle = i18n._(t`Confirm Revoke`);
const revokeText = i18n._(t`Are you sure you want to revoke this item?`);
const revokeTextPlural = i18n._(t`Are you sure you want to revoke these items?`);

export default function ApiTokensList() {
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper } = useFormDeleteWrapper(i18n._(t`Successfully Revoked`));
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();

    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });

    const handleConfirmedDelete = () => {
        const params = _.merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () => formDeleteService.removeSelection(dataSource.url, params, tableSelection),
            refresherFunc: refresher,
        })();
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    return (
        <AdminPage
            title={t`API Tokens`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <Button href={apiDocsUrl} target="_blank">
                        <Trans>API Docs</Trans>
                    </Button>
                    <ConfirmButton
                        disabled={actionsDisabled}
                        handleConfirm={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                        title={revokeTitle}
                        confirmText={revokeText}
                        confirmTextPlural={revokeTextPlural}
                        isWarning
                        confirmButtonText={revokeButtonText}
                    />
                    <Button
                        component={Link}
                        to={buildCreatePageTarget(functionName)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Create</Trans>
                    </Button>
                </Actions>
            }
        >
            <DataTable
                tableId="admin-api-token-list"
                loader={loader}
                triggerRef={triggerRef}
                columns={getColumns()}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
